import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import { BASENAME, LAYOUT_TYPES } from '../../../constants';
import Loading from '../../atoms/Loading';
import LineTitleGray from '../../atoms/titles/LineTitleGray';
import LinkButton from '../../atoms/button/LinkButton';
import Textlink from '../../atoms/link/Textlink';
import ModalWesterPoint from '../../modal/ModalWesterPoint';
import { setCurrentStep, setLayoutType, handleLogin, setErrorMessages } from '../../../modules/app';
import environments from '../../../environments';
import { getSessionId, getSearchSessionKey, getPlanDetailState } from '../../../utils/localStorage';
import { makeSessionMember } from '../../../utils/input';
import { updateSession, useOtherSiteId } from '../../../modules/session';
import { applyErrorStyle, getValidationErrorMessages, removeErrorStyle } from '../../../utils/error';
import { FDataFromSession } from '../../Head';
import { usePrice } from '../../../utils/price';
import { mergeSessionPlansWithPlanDetail } from '../../../utils/plans';

const sessionId = getSessionId();

const Login = ({ session }) => {
  const currentStep = 2;
  const dispatch = useDispatch();
  const { userId, setUserId, password, setPassword } = useLogin();
  const [fetching, setFetching] = useState(false);
  const [showWesterPointModal, setShowWesterPointModal] = useState(false);
  const { westerFlag } = useOtherSiteId();  // westerFlag true の時だけWESTERログイン表示

  useEffect(() => {
    dispatch(
      setLayoutType(LAYOUT_TYPES.step)
    );
    dispatch(setCurrentStep(currentStep));

    return () => {
      dispatch(
        setLayoutType(LAYOUT_TYPES.default)
      );
    };
  }, []);

  const history = useHistory();

  const { people, plans, flightSchedule, lands: planOptions } = session;
  const selectedOutboundDate = flightSchedule.departure.departureTime.substr(0, 10);
  const defaultSortedPlanOptionKind = planOptions.map((goods) => {
    return goods.chargeKinds.map((kind) => {
      if (kind.sort === 0) {
        return {
          ...kind
        };
      }
      return kind;
    })
  });

  const planDetailPlans = getPlanDetailState(getSearchSessionKey()).plans;
  const plansForPrice = mergeSessionPlansWithPlanDetail(plans, planDetailPlans);
  const { peoplePrice } = usePrice(people, flightSchedule.departure, flightSchedule.arrival, plansForPrice, selectedOutboundDate, planOptions, true, defaultSortedPlanOptionKind);
  const representativePrice = peoplePrice[0].price;

  const handleLoginButton = () => {
    setFetching(true);

    removeErrorStyle('pageLogin');

    const onSuccess = async (member) => {
      member.totalPrice = representativePrice;
      const memberSessionInput = makeSessionMember(member);
      const onDone = () => {
        setFetching(false);
        history.push('/entry_customer');
      };

      dispatch(updateSession(memberSessionInput, onDone, onDone));
    };

    const onFailed = (errors) => {
      applyErrorStyle(errors);

      let messages = getValidationErrorMessages(errors);
      if (!messages && errors && errors.length) {
        messages = errors.map((e => e.debugMessage));
      }

      dispatch(setErrorMessages(messages));
      setFetching(false);
    };

    handleLogin(userId, password, onSuccess, onFailed);
  };

  const handleBack = () => {
    history.goBack();
  };

  const handleWesterLogin = () => {
    const westerLoginUrl = `${environments.API_BASE_URI}/westerLogin/redirect?s=${sessionId}`;
    window.open(westerLoginUrl, '_self');
  };

  const backPageUrl = `${environments.API_BASE_URI}/login/callback`;
  const enrollmentSrc = environments.ENROLLMENT_SRC;

  return (
    <div id="pageLogin" className="pageLogin">
      <FDataFromSession session={session} step={currentStep} />
      <Loading show={fetching} hideProgressBar progressRate={100} />
      <LineTitleGray size="lg">会員認証</LineTitleGray>
      <div className="loginContainer">
        <div className="loginItem">
          <div className="loginItemBodyWrapper">
            <LineTitleGray size="sm" border="borderAll">会員登録がお済みのお客様</LineTitleGray>
            <div className="loginItemBody">
              <div className="formBlock">
                <label>ユーザーID/<br className="isPC" />メールアドレス</label>
                <div className="loginForm">
                  <input
                    id="input.loginID"
                    type="email"
                    placeholder="0123456789ABCDEFG"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                  />
                </div>
              </div>
              <div className="formBlock">
                <label>パスワード</label>
                <div className="loginForm">
                  <input
                    id="input.password"
                    type="password"
                    placeholder="パスワードを入力してください"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>
              <div className="loginSup">
              ユーザーIDを忘れた方は
                <Textlink
                  to={environments.DP_USERID_RESET_URI}
                  outgoing
                >
                  こちら
                </Textlink>
              </div>
              <div className="loginSup password">
              パスワードを忘れた方は
                <Textlink
                  to={environments.DP_PASSWORD_RESET_URI}
                  outgoing
                >
                  こちら
                </Textlink>
              </div>
              <div className="loginButton">
                <LinkButton buttonType="ctaMin" onClick={handleLoginButton}>ログインして予約へ進む</LinkButton>
              </div>
            </div>
          </div>
        </div>
        <div className="loginItem notRegistration">
          <div className="loginItemBodyWrapper">
            <LineTitleGray size="sm" border="borderAll">会員登録がまだのお客様</LineTitleGray>
            <div className="loginItemBody">
              <div className="banner">
                <img src="https://www.nta.co.jp/images/bn_kaiinninsho/bnr_yoyaku.gif" alt="日本旅行会員になると" />
              </div>
              <div className="loginSup forgot">
                メールマガジンについて詳しくは
                <Textlink
                  to="https://www.nta.co.jp/mailmagazine/index.html"
                  outgoing
                >
                  こちら
                </Textlink>
              </div>
              <div className="loginButton">
                <form action={environments.DP_SIGNUP_URI} method="POST" acceptCharset="Shift_JIS">
                  <input hidden name="BACK_PAGE" value={backPageUrl} readOnly />
                  <input hidden name="SESSION_INFO" value={sessionId} readOnly />
                  <input hidden name="ENROLLMENT_SRC" value={enrollmentSrc} readOnly />
                  <LinkButton buttonType="ctaMin">会員登録しながら予約へ進む</LinkButton>
                </form>
                <div className="notRegistrationLink">
                  <Textlink to={`${BASENAME}/mail`}>会員登録しないで予約（ゲスト予約）へ進む</Textlink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {westerFlag && <div className="westerLogin">
        <div className="westerLoginTitle">WESTERポイントを使う・貯めるお客様</div>
        <div className="westerLoginContent">
          <a href="https://wester.jr-odekake.net/point/" className="westerLoginImage">
            <img src="https://www.nta.co.jp/images/rsvbnr/bn_rsv_wester.png" alt="WESTERポイント" />
          </a>
          <div className="textLinkWrap">
            ご予約にあたり株式会社日本旅行がお客様のWESTER会員情報を利用します。<br />
            <a href="https://www.nta.co.jp/security.htm" className="textLink" target="_blank" rel="noopener noreferrer">個人情報保護方針</a>に同意の上、下記「WESTERログインして予約」ボタンを押してください。ボタンを押すことで承諾いただいたものとさせていただきます。<br />
          </div>
          <div className="textLinkWrap">
            WESTERポイントのご利用が可能です。詳しくは<a className="textLink showWesterPointModalLink" onClick={() => setShowWesterPointModal(true)}>こちら</a>
          </div>
        </div>
        <div className="westerLoginButton">
          <LinkButton buttonType="ctaMin" onClick={handleWesterLogin}>WESTER ID でログイン</LinkButton>
        </div>
      </div>}

      <div className="backButton">
        <LinkButton buttonType="navigateMin" onClick={handleBack}>申込み内容確認に戻る</LinkButton>
      </div>

      <ModalWesterPoint
        open={showWesterPointModal}
        onClose={() => setShowWesterPointModal(false)}
      />
    </div>
  );
};

Login.propTypes = {
  session: PropTypes.object,
};

export default Login;

const useLogin = () => {
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');

  return { userId, setUserId, password, setPassword };
};
